import { Controller, useFormContext } from "react-hook-form";
import { CBAuth, CBCountry, CBOmissionType, CustomerBaseDto, IFormValues } from "../../types";
import { Accordion, AccordionDetails, AccordionSummary, Grid, IconButton, Stack, Tooltip, Typography } from "@mui/material/index";
import { Fragment, SyntheticEvent, useEffect, useState } from "react";
import { DeleteRounded, DescriptionRounded, ExpandMore } from "@mui/icons-material";
import CBCountryController from "../formController/CBCountryController";
import { isGlobalEditModeState, provincesState, sapEnumState } from "../../state/customerbaseState";
import { useRecoilValue } from "recoil";
import CBProvinceController from "../formController/CBProvinceController";
import CBFormTextFieldController from "../formController/CBFormTextFieldController";
import FormController from "../formController/FormController";
import UpdateBtnsAccordion from "../UpdateBtnsAccordion";

interface CustomerBaseExtraAddressProps {
    index: number,
    isCreationMode: boolean
    onRemove: (event: SyntheticEvent, index: number, selectedExtraAddress: any) => void,
}

const CustomerBaseExtraAddress = ({
    index,
    isCreationMode,
    onRemove,
}: CustomerBaseExtraAddressProps) => {

    const { getValues } = useFormContext<IFormValues>();
    const provinceOptions = useRecoilValue(provincesState);
    const sapEnum = useRecoilValue(sapEnumState);
    const isGlobalEditMode = useRecoilValue(isGlobalEditModeState);

    const businessName = getValues(`cbExtraAddresses.${index}.businessName`);
    const vatNumber = getValues(`cbExtraAddresses.${index}.vatNumber`);
    const accordionTitle = businessName + (!!vatNumber && vatNumber !== CBOmissionType.OMISSION_DASH ? (' - ' + vatNumber) : '');
    
    const linkedCustomerId = getValues(`cbExtraAddresses.${index}.customerId`) || '';
    const country = getValues(`cbExtraAddresses.${index}.country`); 

    return (
        <Accordion>
            <AccordionSummary expandIcon={ <ExpandMore/> }>
                <UpdateBtnsAccordion
                    label={accordionTitle}
                    editing={isGlobalEditMode}
                    isExpanded
                    showCustomIcon={!!linkedCustomerId}
                    customIcon={<DescriptionRounded fontSize="small"/>}
                    onClickCustomIcon={(event) => {
                        event.stopPropagation();
                        window.open(`/customerbase/view/${linkedCustomerId}`, "_blank");
                    }}
                    showRemove={isCreationMode || isGlobalEditMode}
                    onRemove={(event) => onRemove(event, index, getValues(`cbExtraAddresses.${index}`))}
                />
            </AccordionSummary>
            <AccordionDetails>
                <Grid container columnSpacing={3} rowSpacing={1}>
                    <Grid item md={6} xs={12}>
                        <CBCountryController 
                            formName={`cbExtraAddresses.${index}.country`}
                            idField={`cbExtraAddresses-${index}-country`}
                            label={"country"}
                            showSelect={false}
                            readOnly
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBProvinceController
                            formName={`cbExtraAddresses.${index}.province`}
                            idField={`cbExtraAddresses-${index}-province`}
                            label={"province"}
                            provinceOptions={provinceOptions.filter(p => p.country == country).map(p => ({
                                key: p.codeProvince,
                                value: p.description 
                             }))}
                            showSelect={false}
                            readOnly 
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbExtraAddresses.${index}.city`}
                            idField={`cbExtraAddresses-${index}-city`}
                            label={"city"}
                            readOnly
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbExtraAddresses.${index}.zipCode`}
                            idField={`cbExtraAddresses-${index}-zipCode`}
                            label={"zipCode"}
                            readOnly
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbExtraAddresses.${index}.street`}
                            idField={`cbExtraAddresses-${index}-street`}
                            label={"street"}
                            readOnly
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbExtraAddresses.${index}.houseNumber`}
                            idField={`cbExtraAddresses-${index}-houseNumber`}
                            label={"houseNumber"}
                            readOnly
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbExtraAddresses.${index}.addrEmail`}
                            idField={`cbExtraAddresses-${index}-addrEmail`}
                            label={"addrEmail"}
                            readOnly
                        />
                    </Grid>
                    {(country !== CBCountry.FRANCE) &&
                        <>
                            <Grid item md={6} xs={12}>
                                <CBFormTextFieldController 
                                    formName={`cbExtraAddresses.${index}.pec`}
                                    idField={`cbExtraAddresses-${index}-pec`}
                                    label={"pec"}
                                    readOnly
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <CBFormTextFieldController 
                                    formName={`cbExtraAddresses.${index}.recipientCode`}
                                    idField={`cbExtraAddresses-${index}-recipientCode`}
                                    label={"recipientCode"}
                                    readOnly
                                />
                            </Grid>
                        </>
                    }
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbExtraAddresses.${index}.telephone`}
                            idField={`cbExtraAddresses-${index}-telephone`}
                            label={"telephone"}
                            readOnly
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbExtraAddresses.${index}.fax`}
                            idField={`cbExtraAddresses-${index}-fax`}
                            label={"fax"}
                            readOnly
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <CBFormTextFieldController 
                            formName={`cbExtraAddresses.${index}.website`}
                            idField={`cbExtraAddresses-${index}-website`}
                            label={"website"}
                            readOnly
                        />
                    </Grid>
                    <Grid item md={6} xs={12}>
                        <FormController 
                            formName={`cbExtraAddresses.${index}.division`}
                            idField={`cbExtraAddresses-${index}-division`}
                            label="division"
                            translationLabel="sapEnum.divisions"
                            options={sapEnum.divisions || []}
                            showSelect={false}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    )

}

export default CustomerBaseExtraAddress;